import React, { useRef } from 'react';

import styled from '@emotion/styled';

import { ClickOutDropDown } from '../../../../components/DropDown/ClickOutDropDown';
import ThreeDotIcon from '../../../../components/Icons/ThreeDotIcon';
import Img from '../../../../components/Img/Img';
import { IApplicantData } from '../../../../models/dashboard/jobs/singleJobApplicants.model';
import { DEFAULT_IMAGE_URL, timeAgo } from '../../../../utils/utils';
import ReminderIcon from '../../../../components/Icons/ReminderIcon';
import IconTelephone from '../../../../components/Icons/PhoneIcon';
import { ModalProvider } from '../../../../context/ModalContext';
import ModalTriggerContainer from '../../../../components/Modals/ModalTriggerContainer';
import { Modal } from '../../../../components/Modals/Modal';
import ModalContainer from '../../../../components/Modals/ModalContainer';
import useGetApplicantData from '../../../../hooks/dashboard/jobs/useGetApplicantData';
import { LoadingOutlined } from '@ant-design/icons';
import useSendReminder from '../../../../hooks/dashboard/jobs/useSendReminder';

const InterviewCardDropDown = ({
  onRemind,
  onContact,
}: {
  onRemind?: () => void;
  onContact?: () => void;
}) => {
  return (
    <ClickOutDropDown
      tableFilter
      label={
        <IconArea>
          <ThreeDotIcon />
        </IconArea>
      }
      borderRadius="12px"
      posRight="0px"
    >
      <DropDownContent>
        <DropDownCell onClick={onRemind}>
          <ReminderIcon />
          Send Reminder
        </DropDownCell>
        <DropDownCell onClick={onContact}>
          <IconTelephone />
          Contact Locum
        </DropDownCell>
      </DropDownContent>
    </ClickOutDropDown>
  );
};

interface InterviewCardProps extends IApplicantData {}

const AcceptedCard: React.FC<InterviewCardProps> = ({
  createdAt,
  email,
  firstName,
  id,
  lastName,
  resume,
  status,
  updatedAt,
  userId,
}) => {
  const { loading: sending, sendReminder } = useSendReminder();

  const handleRemind = () => {
    sendReminder(id);
  };

  const handleContact = () => {
    if (modalRef) {
      modalRef.current?.click();
    }
  };

  const modalRef = useRef<HTMLDivElement | null>(null);

  // TODO: come back here and check this implementation to get an accepted applicant details

  const { applicantData, loading: dataload } = useGetApplicantData(
    userId,
    modalRef !== null || false
  );

  return (
    <Container>
      <Upper>
        <ImageHolder>
          <Img src={DEFAULT_IMAGE_URL} alt="image" />
        </ImageHolder>
        <div>
          <Name>
            Dr. {firstName} {lastName}
          </Name>
          <AppliedAt>Applied {timeAgo(updatedAt)}</AppliedAt>
        </div>
        <MenuRegion>
          <InterviewCardDropDown
            onRemind={handleRemind}
            onContact={handleContact}
          />
        </MenuRegion>
      </Upper>
      <Bottom>
        <Accept>accepted</Accept>
        {sending && 'sending...'}
      </Bottom>
      <ModalProvider>
        <ModalTriggerContainer ref={modalRef}>
          <></>
        </ModalTriggerContainer>
        <Modal>
          <ModalContainer marginTop="15rem">
            {dataload ? (
              <LoadingOutlined />
            ) : (
              <>
                {' '}
                <P>
                  email: <span>{applicantData?.email}</span>
                </P>
                <P>
                  phone: <span>{applicantData?.phone || 'N/A'}</span>
                </P>
              </>
            )}
          </ModalContainer>
        </Modal>
      </ModalProvider>
    </Container>
  );
};

export default AcceptedCard;

const Container = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.greyGrey4};
  box-shadow: 0px 20px 26px 0px rgba(186, 182, 182, 0.16);
  border-radius: 6px;
`;

const Upper = styled.div`
  display: flex;
  gap: 6px;
  padding: 13px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.greyGrey2};
`;

const ImageHolder = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 16px;
`;

const IconArea = styled.div`
  cursor: pointer;
  /* border: 2px solid red; */
  height: 16px;
  display: grid;
  place-content: center;
`;

const Bottom = styled.div`
  padding: 13px 16px;
  width: 100%;
  display: flex;
  gap: 16px;
`;

const Name = styled.div`
  display: flex;
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.blackBlack2};
`;

const AppliedAt = styled.div`
  display: flex;
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.greyGrey2};
`;

const DropDownContent = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  /* pad */
`;

const DropDownCell = styled.div`
  display: grid;
  grid-template-columns: 16px 132px;
  gap: 10px;
  padding: 8px;
  align-items: center;
  width: 132px;
  font-size: 14px;

  &:first-of-type {
    border-bottom: 1px solid #e9eeef;
    color: ${({ theme }) => theme.palette.stateColorYellow};
  }

  &:hover {
    cursor: pointer;
    background-color: ${({ theme }) => theme.palette.greyGrey4};
  }

  &:last-of-type {
    color: ${({ theme }) => theme.palette.mainPurple};
  }
`;

const MenuRegion = styled.div`
  margin-left: auto;
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: 10px;
`;

const Accept = styled.span`
  width: fit-content;
  width: 6rem;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.palette.lightLemon};
  margin-left: 3rem;
  border-radius: 10px;
  font-size: 18px;
  font-weight: 300;
  line-height: 26.91px;
  letter-spacing: -1px;
  text-align: left;
`;

const P = styled.div`
  font-size: 18px;
  font-weight: 600;

  & > span {
    font-size: 14px;
    font-weight: 400;
  }
`;
